/**
 * meters/lk4410.ts — L&T LK4410 column map (rich, ~104 columns)
 * ==============================================================
 * Sheets: RALTECH (ralpro tech), RALPRO (ralpro 01) — currently in Telangana,
 * but this model can be installed in ANY state; the column map is state-agnostic.
 *
 * Header layout (the live sheet's first row), in order:
 *   Timestamp, Watts_Total, Watts_R, Watts_Y, Watts_B,
 *   VAR_Total, VAR_R, VAR_Y, VAR_B,
 *   True_PF_Avg, True_PF_R, True_PF_Y, True_PF_B,
 *   VA_Total, VA_R, VA_Y, VA_B,
 *   VLL_Avg, V_RY, V_YB, V_BR, VLN_Avg, V_R, V_Y, V_B,
 *   I_Total, I_R, I_Y, I_B, Frequency_Hz,
 *   Wh_Received, VAh_Received, VARh_Ind_Received, VARh_Cap_Received,
 *   Wh_Delivered, VAh_Delivered, VARh_Ind_Delivered, VARh_Cap_Delivered,
 *   PF_Avg_Received, Amps_Hour_Received, PF_Avg_Delivered, Amps_Hour_Delivered,
 *   Neutral_I, V_R_Harmonic, V_Y_Harmonic, V_B_Harmonic,
 *   I_R_Harmonic, I_Y_Harmonic, I_B_Harmonic,
 *   Rising_Demand, Max_Demand, Rising_Demand_Delivered, Displacement_PF_R,
 *   Max_Demand_Delivered, Displacement_PF_B, RPM,
 *   Load_Hours_Received, Load_Hours_Delivered, Num_Interruptions, MD_Occurrence_Time,
 *   V_R_Phase_Angle … I_B_Phase_Angle, V_Unbal_R … I_Unbal_B,
 *   Analog_Input_1, Analog_Input_2,
 *   VLL_Max … PF_Min, V_R_THD_Pct … I_B_THD_Pct, K_Factor_V_R … K_Factor_I_B
 */

import type { RawMeterRow } from '@/types';
import type { MeterModel } from './types';
import { applyAliases } from './helpers';

export const LK4410: MeterModel = {
  code: 'lk4410',
  label: 'L&T LK4410',
  vendor: 'L&T',
  description: 'Full-feature HT meter: VAR, harmonics, THD, K-factor, min/max, phase angles, '
    + 'received/delivered energy registers and meter-native rising/max demand.',
  expectedColumns: 104,

  // Distinctive headers that only the LK4410 carries.
  signatureHeaders: ['VAR_Total', 'Wh_Received', 'K_Factor_I_B', 'True_PF_Avg'],

  fieldMap: {
    wattsTotal: 'Watts_Total', wattsR: 'Watts_R', wattsY: 'Watts_Y', wattsB: 'Watts_B',
    varTotal: 'VAR_Total', varR: 'VAR_R', varY: 'VAR_Y', varB: 'VAR_B',
    truePFAvg: 'True_PF_Avg', truePFR: 'True_PF_R', truePFY: 'True_PF_Y', truePFB: 'True_PF_B',
    vaTotal: 'VA_Total', vaR: 'VA_R', vaY: 'VA_Y', vaB: 'VA_B',
    vllAvg: 'VLL_Avg', vRY: 'V_RY', vYB: 'V_YB', vBR: 'V_BR',
    vlnAvg: 'VLN_Avg', vR: 'V_R', vY: 'V_Y', vB: 'V_B',
    iTotal: 'I_Total', iR: 'I_R', iY: 'I_Y', iB: 'I_B',
    frequencyHz: 'Frequency_Hz',
    whReceived: 'Wh_Received', vahReceived: 'VAh_Received',
    varhIndReceived: 'VARh_Ind_Received', varhCapReceived: 'VARh_Cap_Received',
    whDelivered: 'Wh_Delivered', vahDelivered: 'VAh_Delivered',
    varhIndDelivered: 'VARh_Ind_Delivered', varhCapDelivered: 'VARh_Cap_Delivered',
    pfAvgReceived: 'PF_Avg_Received', ampsHourReceived: 'Amps_Hour_Received',
    pfAvgDelivered: 'PF_Avg_Delivered', ampsHourDelivered: 'Amps_Hour_Delivered',
    neutralI: 'Neutral_I',
    vRHarmonic: 'V_R_Harmonic', vYHarmonic: 'V_Y_Harmonic', vBHarmonic: 'V_B_Harmonic',
    iRHarmonic: 'I_R_Harmonic', iYHarmonic: 'I_Y_Harmonic', iBHarmonic: 'I_B_Harmonic',
    risingDemand: 'Rising_Demand', maxDemandMeter: 'Max_Demand',
    risingDemandDelivered: 'Rising_Demand_Delivered', maxDemandDelivered: 'Max_Demand_Delivered',
    displacementPFR: 'Displacement_PF_R', displacementPFB: 'Displacement_PF_B',
    rpm: 'RPM',
    loadHoursReceived: 'Load_Hours_Received', loadHoursDelivered: 'Load_Hours_Delivered',
    numInterruptions: 'Num_Interruptions', mdOccurrenceTime: 'MD_Occurrence_Time',
    vRPhaseAngle: 'V_R_Phase_Angle', vYPhaseAngle: 'V_Y_Phase_Angle', vBPhaseAngle: 'V_B_Phase_Angle',
    iRPhaseAngle: 'I_R_Phase_Angle', iYPhaseAngle: 'I_Y_Phase_Angle', iBPhaseAngle: 'I_B_Phase_Angle',
    vUnbalR: 'V_Unbal_R', vUnbalY: 'V_Unbal_Y', vUnbalB: 'V_Unbal_B',
    iUnbalR: 'I_Unbal_R', iUnbalY: 'I_Unbal_Y', iUnbalB: 'I_Unbal_B',
    analogInput1: 'Analog_Input_1', analogInput2: 'Analog_Input_2',
    vllMax: 'VLL_Max', vllMin: 'VLL_Min', vlnMax: 'VLN_Max', vlnMin: 'VLN_Min',
    ampsMax: 'Amps_Max', ampsMin: 'Amps_Min', frequencyMax: 'Frequency_Max', frequencyMin: 'Frequency_Min',
    wattsMax: 'Watts_Max', wattsMin: 'Watts_Min', varMax: 'VAR_Max', varMin: 'VAR_Min',
    vaMax: 'VA_Max', vaMin: 'VA_Min', pfMax: 'PF_Max', pfMin: 'PF_Min',
    vRThdPct: 'V_R_THD_Pct', vYThdPct: 'V_Y_THD_Pct', vBThdPct: 'V_B_THD_Pct',
    iRThdPct: 'I_R_THD_Pct', iYThdPct: 'I_Y_THD_Pct', iBThdPct: 'I_B_THD_Pct',
    kFactorVR: 'K_Factor_V_R', kFactorVY: 'K_Factor_V_Y', kFactorVB: 'K_Factor_V_B',
    kFactorIR: 'K_Factor_I_R', kFactorIY: 'K_Factor_I_Y', kFactorIB: 'K_Factor_I_B',
  },

  // Energy registers are already in Wh / VAh on this sheet.
  energyWhScale: 1,
  energyVahScale: 1,

  // The LK4410 reports everything natively; just keep aliases in sync.
  finalizeRaw: (r: RawMeterRow): RawMeterRow => applyAliases(r),
};
